import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { People, PeopleAdapter } from '../../models/people';
import { catchError, combineLatest, debounceTime, map, Observable, of, startWith, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { AvatarComponent } from '../../_utils/components/avatar/avatar.component'; 
import { PeoplesService } from '../../services/peoples.service';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { IProfileSearch } from '../../_interfaces/profile-search';
import { Team, TeamAdapter } from '../../models/team';
import { TeamsService } from '../../services/teams.service';
import { Role, RoleAdapter } from '../../models/role';
import { RolesService } from '../../services/roles.service';
import { PeopleStatusComponent } from "../../_utils/components/people-status/people-status.component";

@Component({
  selector: 'app-select-people-modal',
  standalone: true,
  imports: [CommonModule, TranslatePipe, AvatarComponent, ReactiveFormsModule, PeopleStatusComponent],
  templateUrl: './select-people-modal.component.html', 
  styleUrls: ['./select-people-modal.component.scss']
})
export class SelectPeopleModalComponent implements OnInit {
  @Input() showModal: boolean = false;
  @Input() scopedTeamId: string | null = null;
  @Output() personSelected = new EventEmitter<People | null>();
  @Output() modalClosed = new EventEmitter<void>();

  search = this.fb.nonNullable.group({
    name: [''],
    teamId: [''],
    roleId: ['']
  });

  people$!: Observable<People[]>;
  teamsFilter$: Observable<Team[]> = this.fetchTeams();
  rolesFilter$: Observable<Role[]> = this.fetchRoles();


  errorMessage: string | null = null;

  constructor(private peoplesService: PeoplesService,
              private teamService: TeamsService,
              private roleService: RolesService,
              private teamAdapter: TeamAdapter,
              private roleAdapter: RoleAdapter,
              private fb: FormBuilder, 
              private peopleAdapter: PeopleAdapter) {}

  ngOnInit(): void {
    // Initialise teamId dans le FormGroup AVEC la valeur de l'Input
    this.search.controls.teamId.setValue(this.scopedTeamId || '');
    this.people$ = this.getPeoples();
  }
  
  private fetchPeoples(search: IProfileSearch): Observable<People[]> {
    return this.peoplesService.get_peoples(search).pipe(
      map((data: any[]) => data.map(item => this.peopleAdapter.adapt(item))),
      catchError((error: any) => {
        this.errorMessage = 'Error during peoples fetch : ' + error.message;
        return of([]); // return empty table to avoid binding error
      })
    );
  }

  private fetchTeams():Observable<Team[]>{
    return this.teamService.getTeams().pipe(
      map((data: any[]) => data.map(item => this.teamAdapter.adapt(item))),
      catchError((error: any) => {
        this.errorMessage = 'Error during teams fetch : ' + error.message;
        return of([]); // return empty table to avoid binding error
      })
    );
  }

  private fetchRoles():Observable<Role[]>{
    return this.roleService.getRoles().pipe(
      map((data: any[]) => data.map(item => this.roleAdapter.adapt(item))),
      catchError((error: any) => {
        this.errorMessage = 'Error during roles fetch : ' + error.message;
        return of([]); // return empty table to avoid binding error
      })
    );
  }

  selectPerson(person: People): void {
    this.personSelected.emit(person);
  }

  closeModal(): void {
    this.modalClosed.emit();
  }

  private getPeoples(): Observable<People[]> {
    const search$ = combineLatest([
        this.search.controls.name.valueChanges.pipe(startWith('')),
        this.search.controls.teamId.valueChanges.pipe(startWith(this.search.controls.teamId.value)),
        this.search.controls.roleId.valueChanges.pipe(startWith('')),
      ]).pipe(
        debounceTime(300),
        map(([name, teamId, roleId]) => {
          const filters: IProfileSearch = { name, teamId, roleId, status: '' }; // status is not used in this component
          return filters;
        }),
      );
  
      return search$.pipe(
        switchMap((params) => this.fetchPeoples(params))
        );
    }

  resetSearchField(controlName: string): void {
    this.search.get(controlName)?.setValue('');   
  }

  personTrackBy(index: number, people: People): number {
    return people.id;
  }
}